import { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';

interface AuditLog {
  id: string;
  adminId: string;
  action: string;
  tenantId: string | null;
  details: any;
  performedAt: string;
  admin: {
    email: string;
    displayName: string;
  };
  tenant: {
    name: string;
    domain: string;
  } | null;
}

export default function AuditLogs({ adminUser }: any) {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [actions, setActions] = useState<string[]>([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  useEffect(() => {
    fetchActions();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, selectedAction]);

  const fetchActions = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/audit-logs/actions`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setActions(data.data);
      }
    } catch (error) {
      console.error('Error fetching actions:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
      });
      if (selectedAction) params.append('action', selectedAction);

      const response = await fetch(`${apiUrl}/api/audit-logs?${params.toString()}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.data.logs);
        setPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  const getActionColor = (action: string) => {
    if (action.includes('SUSPEND') || action.includes('DELETE')) return '#ef4444';
    if (action.includes('CREATE') || action.includes('REACTIVATE')) return '#10b981';
    if (action.includes('UPDATE') || action.includes('EXTEND')) return '#3b82f6';
    return '#6b7280';
  };

  return (
    <>
      <Navigation adminUser={adminUser} />
      <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', background: '#f9fafb', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
            Audit Logs
          </h1>
          <select
            value={selectedAction}
            onChange={(e) => { setSelectedAction(e.target.value); setPage(1); }}
            style={{
              padding: '10px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="">All Actions</option>
            {actions.map((action) => (
              <option key={action} value={action}>{action}</option>
            ))}
          </select>
        </div>

        <div style={cardStyle}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              Loading audit logs...
            </div>
          ) : logs.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
              No audit logs found
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Time
                      </th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Admin
                      </th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Action
                      </th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Tenant
                      </th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '14px', whiteSpace: 'nowrap' }}>
                          {new Date(log.performedAt).toLocaleString('da-DK', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td style={{ padding: '12px 8px', color: '#111827', fontSize: '14px' }}>
                          <div style={{ fontWeight: '500' }}>{log.admin.displayName}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{log.admin.email}</div>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: `${getActionColor(log.action)}20`,
                            color: getActionColor(log.action),
                          }}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '14px' }}>
                          {log.tenant ? (
                            <div>
                              <div style={{ fontWeight: '500', color: '#111827' }}>{log.tenant.name}</div>
                              <div style={{ fontSize: '12px' }}>{log.tenant.domain}</div>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <button
                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                            style={{
                              padding: '4px 12px',
                              background: '#f3f4f6',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              color: '#374151',
                            }}
                          >
                            {expandedLog === log.id ? 'Hide' : 'Show'}
                          </button>
                          {expandedLog === log.id && (
                            <div style={{
                              marginTop: '8px',
                              padding: '12px',
                              background: '#f9fafb',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                            }}>
                              {JSON.stringify(log.details, null, 2)}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '24px',
                  paddingTop: '24px',
                  borderTop: '1px solid #e5e7eb',
                }}>
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: page === 1 ? '#f3f4f6' : 'white',
                      color: page === 1 ? '#9ca3af' : '#374151',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                    disabled={page === pagination.totalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: page === pagination.totalPages ? '#f3f4f6' : 'white',
                      color: page === pagination.totalPages ? '#9ca3af' : '#374151',
                      cursor: page === pagination.totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
